/**
 * The measurable-root contract for a `<code-island>`: where a code component's
 * rendered content lives inside its shadow root, and which of it paints.
 *
 * Kept in its own module so every caller can share it without importing the
 * rest of `inner-node-utils` — `innerNodePath` needs the selector, and
 * `shadowNodeMeasurement` imports `innerNodePath`.
 */

/**
 * Selector for the wrapper a code island renders its component into.
 *
 * The wrapper is `display: contents`, so it generates no box of its own and is
 * never the thing to measure — the component's own root elements sit inside it.
 */
export const CODE_ISLAND_CONTENT_ROOT_SELECTOR = '[data-root]';

/**
 * The elements a code component actually paints: the children of its
 * content-root wrapper, minus the style carriers that generate no box.
 *
 * `CodeIslandCustomElement` reads this to decide what to observe and report
 * dimensions for, the Frame measurement pass reads it to decide how much of its
 * frame a component occupies, and the visual-editing popover reads it to place
 * itself. All of them need the same answer, and none can get it from the shadow
 * root's own children: those are the wrapper and any `<style>`, all of which
 * measure zero however large the component is.
 */
export const getCodeIslandContentElements = (
  shadowRoot: ShadowRoot
): Element[] => {
  const rootElement = shadowRoot.querySelector(
    CODE_ISLAND_CONTENT_ROOT_SELECTOR
  );

  return Array.from(rootElement?.children ?? []).filter((child) => {
    if (child.nodeType !== Node.ELEMENT_NODE) return false;

    const tagName = child.tagName.toLowerCase();
    return tagName !== 'style' && tagName !== 'link';
  });
};
